<!-- No cases message -->
<div
  class="flex-row align-items-center justify-content-center"
  style="margin: 0 auto; padding: 48px"
  *ngIf="noCases && !loading"
>
  {{ 'table.cases.empty' | translate }}
</div>

<!-- Table content wrapper with loading overlay -->
<div class="table-wrapper" [class.loading]="loading" [style.min-height.px]="containerHeight" *ngIf="!noCases">
  <!-- Loading overlay - preserves the space while showing loading state -->
  <div class="loading-overlay" *ngIf="loading">
    <div class="loading-content">
      <i class="ri-loader-4-line spin"></i>
      {{ 'info.loading_data' | translate }}
    </div>
  </div>

  <!-- Table content - visible but dimmed during loading -->
  <table
    mat-table
    #table
    [dataSource]="items"
    [trackBy]="trackByCaseId"
    class="spotlab-table"
    style="width: 100%"
    [class.dimmed]="loading"
  >
    <!-- State Column -->
    <ng-container matColumnDef="state">
      <th mat-header-cell *matHeaderCellDef>
        {{ 'table.cases.columns.state' | translate | uppercase }}
      </th>
      <td mat-cell *matCellDef="let item">
        <mat-spinner *ngIf="item.state === undefined" diameter="16"></mat-spinner>
        <span
          *ngIf="item.state !== undefined"
          class="state-badge"
          [class.state-pending]="item.state === 'pending'"
          [class.state-read]="item.state === 'read'"
          [class.state-inProgress]="item.state === 'inProgress'"
          [class.state-analyzed]="item.state === 'analyzed'"
        >
          {{ item.state | translate | uppercase }}
        </span>
      </td>
    </ng-container>

    <!-- Case Name Column -->
    <ng-container matColumnDef="name">
      <th mat-header-cell *matHeaderCellDef>
        <button
          type="button"
          class="sortable-header"
          [class.active]="activeSort?.field === 'name'"
          [class.disabled]="!isSortable('name')"
          [disabled]="!isSortable('name')"
          (click)="onHeaderClick('name')"
          data-cy="case-sort-name"
        >
          <span>{{ 'table.cases.columns.name' | translate | uppercase }}</span>
          <i
            class="sort-arrow"
            [class.ri-arrow-up-line]="arrowFor('name') === 'asc'"
            [class.ri-arrow-down-line]="arrowFor('name') === 'desc'"
            [class.ri-sort-asc]="arrowFor('name') === null && isSortable('name')"
            [class.idle]="arrowFor('name') === null"
            aria-hidden="true"
          ></i>
        </button>
      </th>
      <td mat-cell *matCellDef="let item" class="name-cell">
        <span class="case-name-text">{{ item.name }}</span>
      </td>
    </ng-container>

    <!-- Created Column -->
    <ng-container matColumnDef="created">
      <th mat-header-cell *matHeaderCellDef>
        <button
          type="button"
          class="sortable-header"
          [class.active]="activeSort?.field === 'created'"
          (click)="onHeaderClick('created')"
          data-cy="case-sort-created"
        >
          <span>{{ 'table.cases.columns.created' | translate | uppercase }}</span>
          <i
            class="sort-arrow"
            [class.ri-arrow-up-line]="arrowFor('created') === 'asc'"
            [class.ri-arrow-down-line]="arrowFor('created') === 'desc'"
            [class.ri-sort-asc]="arrowFor('created') === null && isSortable('created')"
            [class.idle]="arrowFor('created') === null"
            aria-hidden="true"
          ></i>
        </button>
      </th>
      <td mat-cell *matCellDef="let item">
        <mat-spinner *ngIf="item.createdBy === undefined" diameter="16"></mat-spinner>
        <ts-cell-created-by
          *ngIf="item.createdBy !== undefined"
          [createdBy]="{ user: item.createdBy, date: item.createdAt }"
          [matTooltip]="
            'label.acquired_by_x_on_y'
              | translate
                : {
                    user: item.createdBy?.username || ('table.cases.values.unknown' | translate),
                    date: item.createdAt | date : 'dd/MM/yyyy hh:mm a'
                  }
          "
          [matTooltipShowDelay]="800"
        ></ts-cell-created-by>
      </td>
    </ng-container>

    <!-- Analyses Column (analyzed first, then analysing; same color code as state column) -->
    <ng-container matColumnDef="analyses">
      <th mat-header-cell *matHeaderCellDef>
        {{ 'table.cases.columns.analyses' | translate | uppercase }}
      </th>
      <td mat-cell *matCellDef="let item" class="analyses-cell last-data-cell">
        <mat-spinner *ngIf="item.analysing === undefined || item.analyzed === undefined" diameter="16"></mat-spinner>
        <span
          *ngIf="item.analysing !== undefined && item.analyzed !== undefined"
          class="flex-row align-items-center analyst-list analyses-cell-content"
        >
          <span class="analyst-group">
            <ts-user-avatar
              *ngFor="let user of item.analyzed"
              [user]="user"
              [class.analyzed]="true"
              [matTooltip]="user?.username || ('table.cases.values.unknown' | translate)"
              [matTooltipShowDelay]="800"
            ></ts-user-avatar>
          </span>
          <span class="analyst-group">
            <ts-user-avatar
              *ngFor="let user of item.analysing"
              [user]="user"
              [class.in-progress]="true"
              [matTooltip]="user?.username || ('table.cases.values.unknown' | translate)"
              [matTooltipShowDelay]="800"
            ></ts-user-avatar>
          </span>
        </span>
      </td>
    </ng-container>

    <!-- Migrate Action Column -->
    <ng-container matColumnDef="migrate">
      <th mat-header-cell *matHeaderCellDef></th>
      <td mat-cell *matCellDef="let item" class="icon-cell">
        <button
          *ngIf="canActOnCase(item)"
          class="icon-button"
          type="button"
          (click)="migrateItem.emit(item); $event.stopPropagation()"
          [matTooltip]="'table.cases.columns.migrate' | translate"
          [matTooltipShowDelay]="800"
          data-cy="case-migrate"
        >
          <i class="ri-folder-transfer-line"></i>
        </button>
      </td>
    </ng-container>

    <!-- Copy Action Column -->
    <ng-container matColumnDef="copy">
      <th mat-header-cell *matHeaderCellDef></th>
      <td mat-cell *matCellDef="let item" class="icon-cell">
        <button
          class="icon-button"
          type="button"
          (click)="copyItem.emit(item); $event.stopPropagation()"
          [matTooltip]="'table.cases.columns.copy' | translate"
          [matTooltipShowDelay]="800"
          data-cy="case-copy"
        >
          <i class="ri-file-copy-2-line"></i>
        </button>
      </td>
    </ng-container>

    <!-- Remove Action Column -->
    <ng-container matColumnDef="remove">
      <th mat-header-cell *matHeaderCellDef></th>
      <td mat-cell *matCellDef="let item" class="icon-cell">
        <button
          *ngIf="canActOnCase(item)"
          class="icon-button danger"
          type="button"
          (click)="removeItem.emit(item); $event.stopPropagation()"
          [matTooltip]="'table.cases.columns.remove' | translate"
          [matTooltipShowDelay]="800"
          data-cy="case-remove"
        >
          <i class="ri-delete-bin-6-line"></i>
        </button>
      </td>
    </ng-container>

    <!-- Column Filter Column (Always Visible) -->
    <ng-container matColumnDef="columnFilter">
      <th mat-header-cell *matHeaderCellDef class="column-filter-header">
        <div class="column-filter-wrapper" #filterWrapper>
          <button mat-icon-button class="column-filter-button" (click)="toggleFilterDropdown($event)">
            <i class="ri-layout-column-line"></i>
          </button>
          <!-- Column filter dropdown -->
          <div class="column-filter-menu" *ngIf="showColumnFilter" (click)="$event.stopPropagation()">
            <mat-checkbox *ngFor="let col of dataColumns" [checked]="isColumnVisible(col)" (change)="toggleColumn(col)">
              {{ 'table.cases.columns.' + col | translate }}
            </mat-checkbox>
          </div>
        </div>
      </th>
      <td mat-cell *matCellDef="let item" class="column-filter-cell"></td>
    </ng-container>

    <tr mat-header-row *matHeaderRowDef="visibleColumns"></tr>
    <tr
      mat-row
      *matRowDef="let item; columns: visibleColumns"
      [class.hoverable-row]="true"
      (click)="selectItem.emit(item)"
    ></tr>
  </table>
</div>
